import { useCallback, useEffect, useState } from "react";

import { AccountInfo, AuthError } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useNavigate } from "@tanstack/react-router";
import { useDispatch } from "react-redux";

import { authClient } from "../api/authClient";
import { logoutApiV1AuthLogoutPost } from "../api/generated/sdk.gen";
import { loginRequest, logoutRequest } from "../authConfig";
import { AUTH_ERRORS, DEFAULT_PROTECTED_ROUTE } from "../constants/auth";
import { store } from "../store";
import { hasValidAppToken } from "../utils/appToken";
import { clearRecordingRecoveryData } from "../utils/clearRecordingRecovery";
import {
  clearAuthRelatedLocalStorage,
  clearExplicitLogoutMarker,
  deleteAccessTokenCookie,
  hasValidToken,
  markExplicitLogout,
  setAccessTokenCookie,
} from "../utils/cookies";
import { showError } from "../utils/notification";
import {
  blockRecentSearchWrites,
  clearRecentSearches,
} from "../utils/recentSearchesStorage";
import { authDebugError } from "../utils/safeAuthLog";
import { runSessionTeardown } from "../utils/sessionTeardown";

interface UseAuthReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => void;
  logoutWithNavigation: () => void;
  user: AccountInfo | null;
}

/**
 * Custom hook for authentication management
 * Simplifies auth operations and provides a clean interface
 */
export const useAuth = (): UseAuthReturn => {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const dispatch = useDispatch<typeof store.dispatch>();

  const [isLoading, setIsLoading] = useState(false);

  // Get the current user from accounts
  const user = accounts.length > 0 ? accounts[0] : null;

  // Login function
  const login = useCallback(async () => {
    if (isLoading || isAuthenticated) return;

    setIsLoading(true);
    clearExplicitLogoutMarker();
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      authDebugError("useAuth: login error", error);
      showError(AUTH_ERRORS.LOGIN_FAILED, dispatch);
      setIsLoading(false);
    }
  }, [instance, isLoading, isAuthenticated, dispatch]);

  // Logout function (direct logout)
  const logout = useCallback(async () => {
    if (isLoading) return;

    setIsLoading(true);
    // External (email-code) users have no Microsoft account — decide BEFORE we
    // clear the token. They must NOT be sent to the Microsoft logout page.
    const isExternal = hasValidAppToken() || accounts.length === 0;
    try {
      // Deliberate logout — must stick even through a refresh/new tab (see
      // index.jsx's bootstrap), unlike a defensive/automatic logout. Stays
      // FIRST and synchronous: the recovery clear below can take up to a
      // second, and a user who closes the tab inside that window would
      // otherwise leave no marker and get silently signed back in.
      markExplicitLogout();

      // Clear tokens and local data
      deleteAccessTokenCookie();
      // Clinical search queries (MDRS-558). Synchronous, ahead of the recovery
      // clear below, so closing the tab mid-logout can't leave them behind.
      blockRecentSearchWrites();
      clearRecentSearches();

      // Interrupted-recording and dictation PHI must not survive a logout that
      // fails partway (MDRS-571).
      await clearRecordingRecoveryData();

      // Clear MSAL cache. Guarded on its own, like tokenManager.logoutUser
      // does: a SecurityError here (blocked or partitioned storage, private
      // mode) would otherwise abort the rest of this block, leaving the MSAL
      // cache in sessionStorage, pop-out windows still rendering case content,
      // and — on the internal path — no logoutRedirect at all.
      try {
        sessionStorage.clear();
      } catch (storageError) {
        authDebugError("useAuth: sessionStorage clear failed", storageError);
      }
      clearAuthRelatedLocalStorage();
      // Cached case data and pop-out windows (MDRS-486).
      runSessionTeardown();

      if (isExternal) {
        // Real server-side revocation (MDRS-556) — the refresh-token family
        // tied to this browser's httpOnly cookie. Best-effort: a failed
        // network call must not block the rest of logout, and this is
        // deliberately only reachable from this explicit, user-initiated
        // logout — never from tokenManager.ts's defensive logoutUser() paths
        // (MDRS-383's cba2ce0b: revoking from ~15 defensive call sites turned
        // one flaky request anywhere into a permanent, surprise sign-out).
        try {
          await logoutApiV1AuthLogoutPost({ client: authClient });
        } catch (e) {
          authDebugError("useAuth: backend session revoke failed", e);
        }

        // Also clear any cached MSAL account so a leftover Microsoft SSO session
        // in this browser doesn't silently auto-log the user back in — but do
        // NOT navigate to the Microsoft logout page (onRedirectNavigate:false
        // cancels the redirect while still clearing local MSAL state).
        try {
          await instance.logoutRedirect({
            onRedirectNavigate: () => false,
            postLogoutRedirectUri: window.location.origin + "/",
          });
        } catch (e) {
          authDebugError("useAuth: external MSAL cache clear failed", e);
        }
        window.location.href = "/";
        return;
      }

      // Pass an explicit logoutHint so Azure AD can end this specific session
      // directly instead of falling back to a "pick an account to sign out
      // of" confirmation page (an extra manual step a user can walk away
      // from without ever completing, leaving their Microsoft session
      // alive). `account` alone does NOT achieve this: MSAL only derives
      // logoutHint from the account's ID token `login_hint` claim, which is
      // a non-default optional claim this app registration doesn't request
      // (confirmed: `optionalClaims.idToken` is empty) — so passing `account`
      // without also setting `logoutHint` silently adds no hint at all.
      // `account.username` is always populated, unlike that optional claim.
      // Fall back to omitting logoutHint (today's behavior) rather than
      // sending an empty one on the off chance `username` is ever blank.
      //
      // NOTE (MDRS-383): even with a correct logoutHint, Azure AD can still
      // show this confirmation page for accounts with device-level trust
      // ("Connected to Windows" / Windows Hello for Business) — hints only
      // resolve *which account*, not *whether to disturb device trust*.
      // That's exactly why markExplicitLogout() above matters: a user who
      // abandons that page mid-flow still needs this tab's next load to NOT
      // silently re-authenticate via ssoSilent.
      const account = instance.getActiveAccount() ?? accounts[0];
      await instance.logoutRedirect({
        ...logoutRequest,
        account,
        logoutHint: account.username || undefined,
      });
    } catch (error) {
      authDebugError("useAuth: logout error", error);
      setIsLoading(false);
      if (isExternal) window.location.href = "/";
    }
  }, [instance, isLoading, accounts]);

  // Logout with navigation (navigate to signing out page)
  const logoutWithNavigation = useCallback(() => {
    navigate({ to: "/signing-out" });
  }, [navigate]);

  // Handle redirect after login
  useEffect(() => {
    const handleRedirect = async () => {
      try {
        // Always handle redirect promise - MSAL will return null if no redirect is pending
        // This is safe to call multiple times and must be called to complete the login flow
        const response = await instance.handleRedirectPromise();

        if (response?.account) {
          // User just completed login, set active account
          instance.setActiveAccount(response.account);
        }

        if (response?.accessToken) {
          setAccessTokenCookie(response.accessToken);
        }
      } catch (error) {
        authDebugError("useAuth: handle redirect error", error);

        // Check if this is a state not found error
        if (error instanceof AuthError && error.errorCode === "state_not_found") {
          // Don't clear auth state or redirect - user might be on landing page legitimately
          // Just log and continue
        } else {
          // Only show error for actual authentication failures
          authDebugError("useAuth: authentication redirect error", error);
          // Don't automatically redirect - let the user stay on the current page
        }
      }
    };

    handleRedirect();
  }, [instance, dispatch, navigate]);

  // Auto-redirect authenticated users
  useEffect(() => {
    if (isAuthenticated && hasValidToken()) {
      const currentPath = window.location.pathname;
      if (currentPath === "/" || currentPath === "/auth-callback") {
        navigate({ to: DEFAULT_PROTECTED_ROUTE, replace: true });
      }
    }
  }, [isAuthenticated, navigate]);

  return {
    isAuthenticated,
    isLoading,
    login,
    logout,
    logoutWithNavigation,
    user,
  };
};
